import { useCallback, useMemo } from 'react';
import { isValuelessOperator } from '../types';
import type {
  TableQueryAction,
  FilterValue,
  TableQueryState,
  SortState,
} from '../types';

export interface DataTableStateActions {
  setPage: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  nextPage: (currentIndex: number) => void;
  previousPage: (currentIndex: number) => void;
  firstPage: () => void;
  lastPage: (totalPages: number) => void;
  addFilter: (filter: FilterValue) => void;
  updateFilter: (index: number, filter: FilterValue) => void;
  removeFilter: (index: number) => void;
  setFilters: (filters: FilterValue[]) => void;
  setSort: (sort: SortState) => void;
  toggleSort: (field: string, order?: SortState['order']) => void;
  clearSort: () => void;
  setSearch: (search: string) => void;
  clearSearch: () => void;
  setState: (state: TableQueryState) => void;
  clearFilters: () => void;
  setFilter: (filter: FilterValue) => void;
  toggleRowExpansion: (rowId: string) => void;
  collapseAllRows: () => void;
  setExpandedRowIds: (expandedRowIds: Set<string>) => void;
  reset: () => void;
}

/**
 * Hook that provides action creators for DataTable state management.
 * Encapsulates the dispatch implementation details and provides a clean API.
 *
 * @param dispatch - The dispatch function from useDataTableState
 * @param currentFilters - Current filters array for optimized filter operations
 * @returns Action creator functions for table state updates
 *
 * @example
 * ```tsx
 * const { state, dispatch } = useDataTableState();
 * const actions = useDataTableStateActions(dispatch, state.filters);
 *
 * // Use clean action creators instead of raw dispatch
 * actions.setPage(2);
 * actions.setPageSize(50);
 * actions.nextPage(state.pagination.pageIndex);
 * actions.setFilter(filter); // Intelligently adds, updates or removes
 * ```
 */
export function useDataTableStateActions(
  dispatch: React.Dispatch<TableQueryAction>,
  currentFilters: FilterValue[] = []
): DataTableStateActions {
  const setPage = useCallback(
    (pageIndex: number) => {
      dispatch({ type: 'SET_PAGE', payload: pageIndex });
    },
    [dispatch]
  );

  const setPageSize = useCallback(
    (pageSize: number) => {
      dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize });
    },
    [dispatch]
  );

  const nextPage = useCallback(
    (currentIndex: number) => {
      dispatch({ type: 'SET_PAGE', payload: currentIndex + 1 });
    },
    [dispatch]
  );

  const previousPage = useCallback(
    (currentIndex: number) => {
      dispatch({ type: 'SET_PAGE', payload: Math.max(0, currentIndex - 1) });
    },
    [dispatch]
  );

  const firstPage = useCallback(() => {
    dispatch({ type: 'SET_PAGE', payload: 0 });
  }, [dispatch]);

  const lastPage = useCallback(
    (totalPages: number) => {
      dispatch({ type: 'SET_PAGE', payload: Math.max(0, totalPages - 1) });
    },
    [dispatch]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const addFilter = useCallback(
    (filter: FilterValue) => {
      dispatch({ type: 'ADD_FILTER', payload: filter });
    },
    [dispatch]
  );

  const updateFilter = useCallback(
    (index: number, filter: FilterValue) => {
      dispatch({ type: 'UPDATE_FILTER', payload: { index, filter } });
    },
    [dispatch]
  );

  const removeFilter = useCallback(
    (index: number) => {
      dispatch({ type: 'REMOVE_FILTER', payload: index });
    },
    [dispatch]
  );

  const setFilters = useCallback(
    (filters: FilterValue[]) => {
      dispatch({ type: 'SET_FILTERS', payload: filters });
    },
    [dispatch]
  );

  const setSort = useCallback(
    (sort: SortState) => {
      dispatch({ type: 'SET_SORT', payload: sort });
    },
    [dispatch]
  );

  const toggleSort = useCallback(
    (field: string, order?: SortState['order']) => {
      dispatch({ type: 'TOGGLE_SORT', payload: { field, order } });
    },
    [dispatch]
  );

  const clearSort = useCallback(() => {
    dispatch({ type: 'CLEAR_SORT' });
  }, [dispatch]);

  const setSearch = useCallback(
    (search: string) => {
      dispatch({ type: 'SET_SEARCH', payload: search });
    },
    [dispatch]
  );

  const clearSearch = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH' });
  }, [dispatch]);

  const setState = useCallback(
    (state: TableQueryState) => {
      dispatch({ type: 'SET_STATE', payload: state });
    },
    [dispatch]
  );

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, [dispatch]);

  const toggleRowExpansion = useCallback(
    (rowId: string) => {
      dispatch({ type: 'TOGGLE_ROW_EXPANSION', payload: rowId });
    },
    [dispatch]
  );

  const collapseAllRows = useCallback(() => {
    dispatch({ type: 'COLLAPSE_ALL_ROWS' });
  }, [dispatch]);

  const setExpandedRowIds = useCallback(
    (expandedRowIds: Set<string>) => {
      dispatch({ type: 'SET_EXPANDED_ROW_IDS', payload: expandedRowIds });
    },
    [dispatch]
  );

  // Create a Map of field -> index for O(1) filter lookups
  const filterIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    currentFilters.forEach((filter, index) => {
      map.set(filter.field, index);
    });
    return map;
  }, [currentFilters]);

  /**
   * Intelligently set a filter - adds new, updates existing, or removes if empty.
   * Uses O(1) Map lookup instead of O(n) array search.
   */
  const setFilter = useCallback(
    (filter: FilterValue) => {
      const existingIndex = filterIndexMap.get(filter.field);
      const hasExistingFilter = existingIndex !== undefined;
      const isComplete =
        filter.value.length > 0 || isValuelessOperator(filter.operator);

      if (!isComplete) {
        // Remove filter if no values
        if (hasExistingFilter) {
          dispatch({ type: 'REMOVE_FILTER', payload: existingIndex });
        }
      } else if (hasExistingFilter) {
        // Update existing filter
        dispatch({
          type: 'UPDATE_FILTER',
          payload: { index: existingIndex, filter },
        });
      } else {
        // Add new filter
        dispatch({ type: 'ADD_FILTER', payload: filter });
      }
    },
    [filterIndexMap, dispatch]
  );

  return {
    setPage,
    setPageSize,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    addFilter,
    updateFilter,
    removeFilter,
    setFilters,
    setSort,
    toggleSort,
    clearSort,
    setSearch,
    clearSearch,
    setState,
    clearFilters,
    setFilter,
    toggleRowExpansion,
    collapseAllRows,
    setExpandedRowIds,
    reset,
  };
}
